import React, { useState, useRef, useEffect } from 'react';
import {
    Text,
    Box,
    Loader,
    ActionIcon,
    Tooltip,
    Group,
    Stack,
    NumberInput,
} from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import { Card, Button, Badge } from '@xplatform/design-system';
import {
    IconChevronLeft,
    IconChevronRight,
    IconZoomIn,
    IconZoomOut,
    IconFocusCentered,
    IconScan,
    IconFileText,
} from '@tabler/icons-react';
import './ImageViewer.scss';
import { useExtraction } from '@src/hooks/useContext';
import { useTranslation } from 'react-i18next';
import ImageDisplay from '@src/components/ImageDisplay/ImageDisplay';
import type { ExtractionOptions } from '@src/hooks/extraction/useExtraction';
import type { SharePointSelection } from '@src/services/sharepointApi';

interface ImageViewerProps {
    uploadedFile?: File;
    selectedSharePointFolder?: SharePointSelection;
    onFolderSelect?: (folder: SharePointSelection | undefined) => void;
    extractionOptions?: ExtractionOptions;
    onFileUpload?: (file: File | undefined) => void;
    onOptionsChange?: (options: ExtractionOptions) => void;
    /** Review link: one document is already selected, nothing to upload or run. */
    reviewMode?: boolean;
}

const ImageViewer: React.FC<ImageViewerProps> = ({
    uploadedFile,
    selectedSharePointFolder,
    onFolderSelect,
    extractionOptions = {},
    onFileUpload,
    onOptionsChange,
    reviewMode = false,
}) => {
    const { t } = useTranslation();
    const {
        selectPage,
        startExtraction,
        startSharePointImport,
        doctypeCode,
        selectedNode,
        currentPage,
        totalPages,
        extractedResult,
        isLoadingResult,
        isExtracting
    } = useExtraction();

    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });
    const [goToPageValue, setGoToPageValue] = useState<string | number>(currentPage);
    const [isGoToPageInvalid, setIsGoToPageInvalid] = useState(false);

    const imageContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, [extractedResult?.previewUrl, currentPage]);

    useEffect(() => {
        setGoToPageValue(currentPage);
        setIsGoToPageInvalid(false);
    }, [currentPage]);

    const handlePreviousPage = () => {
        const newPage = currentPage - 1;
        if (newPage >= 1) {
            selectPage(newPage);
        }
    };

    const handleNextPage = () => {
        const newPage = currentPage + 1;
        if (newPage <= totalPages) {
            selectPage(newPage);
        }
    };

    const handleGoToPageSubmit = () => {
        const page = Number(goToPageValue);
        if (!Number.isInteger(page) || page < 1 || page > totalPages) {
            setIsGoToPageInvalid(true);
            return;
        }
        setIsGoToPageInvalid(false);
        if (page !== currentPage) {
            selectPage(page);
        }
    };

    const handleZoomIn = () => {
        setZoom(prev => Math.min(prev * 1.2, 5));
    };

    const handleZoomOut = () => {
        setZoom(prev => Math.max(prev / 1.2, 0.1));
    };

    const handleResetZoom = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    const handleWheel = (e: React.WheelEvent) => {
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom(prev => Math.min(Math.max(prev * delta, 0.1), 5));
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
        setLastPanPoint({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging) {
            setPan({
                x: e.clientX - lastPanPoint.x,
                y: e.clientY - lastPanPoint.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    const isDisplayPagination = totalPages > 1;

    useHotkeys([
        [
            'ArrowLeft',
            () => {
                if (isDisplayPagination && !isLoadingResult) {
                    handlePreviousPage();
                }
            },
        ],
        [
            'ArrowRight',
            () => {
                if (isDisplayPagination && !isLoadingResult) {
                    handleNextPage();
                }
            },
        ],
    ]);

    const isButtonDisabled = !(uploadedFile || selectedSharePointFolder) ||
        isExtracting ||
        !doctypeCode;

    const getTooltipMessage = () => {
        if (!uploadedFile && !selectedSharePointFolder) return t('extraction.imageViewer.tooltips.uploadFileFirst');
        if (!doctypeCode) return t('extraction.imageViewer.tooltips.selectDoctypeFirst');
        if (isExtracting) return t('extraction.imageViewer.tooltips.extractionInProgress');
        return '';
    };

    return (
        <Card className="image-viewer">
            <Stack p="md" h="100%">
                <Group justify="space-between">
                    <Group gap="sm">
                        {!reviewMode && (
                        <Tooltip label={getTooltipMessage()} disabled={!getTooltipMessage()}>
                            <span>
                                <Button
                                    size="xs"
                                    onClick={async () => {
                                        if (!doctypeCode) return;
                                        if (selectedSharePointFolder) {
                                            const started = await startSharePointImport(
                                                selectedSharePointFolder.path,
                                                selectedSharePointFolder.name,
                                                extractionOptions,
                                                selectedSharePointFolder.filePaths,
                                            );
                                            if (started) {
                                                onFolderSelect?.(undefined);
                                                onOptionsChange?.({});
                                            }
                                        } else if (uploadedFile) {
                                            await startExtraction(uploadedFile, extractionOptions);
                                            onFileUpload?.(undefined);
                                            onOptionsChange?.({});
                                        }
                                    }}
                                    disabled={isButtonDisabled}
                                    leftSection={isExtracting ? <Loader size={16} /> : <IconScan size={16} />}
                                >
                                    {isExtracting ? t('extraction.imageViewer.buttons.processing') : t('extraction.imageViewer.buttons.runExtraction')}
                                </Button>
                            </span>
                        </Tooltip>
                        )}
                    </Group>
                    <Group gap="md">
                        <Group gap={2}>
                            <Tooltip label={t('extraction.imageViewer.tooltips.zoomOut')}>
                                <ActionIcon variant="outline" size="sm" onClick={handleZoomOut} disabled={zoom <= 0.1}>
                                    <IconZoomOut size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('extraction.imageViewer.tooltips.resetZoom')}>
                                <ActionIcon variant="outline" size="sm" onClick={handleResetZoom}>
                                    <IconFocusCentered size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('extraction.imageViewer.tooltips.zoomIn')}>
                                <ActionIcon variant="outline" size="sm" onClick={handleZoomIn} disabled={zoom >= 5}>
                                    <IconZoomIn size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>

                        {isDisplayPagination && (
                            <Group gap={4}>
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={handlePreviousPage}
                                    disabled={currentPage <= 1 || isLoadingResult}
                                    leftSection={<IconChevronLeft size={16} />}
                                >
                                    {t('extraction.imageViewer.buttons.previous')}
                                </Button>
                                <NumberInput
                                    size="xs"
                                    w={72}
                                    min={1}
                                    max={totalPages}
                                    clampBehavior="none"
                                    hideControls
                                    value={goToPageValue}
                                    disabled={isLoadingResult}
                                    error={isGoToPageInvalid}
                                    aria-label={t('extraction.imageViewer.pagination.goToPage.ariaLabel')}
                                    rightSection={
                                        <Text component="span" size="xs" c="dimmed" pr={4}>
                                            {t('extraction.imageViewer.pagination.goToPage.ofTotal', { total: totalPages })}
                                        </Text>
                                    }
                                    rightSectionWidth={46}
                                    onChange={(value) => {
                                        setGoToPageValue(value);
                                        setIsGoToPageInvalid(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleGoToPageSubmit();
                                        }
                                    }}
                                    onBlur={handleGoToPageSubmit}
                                />
                                <Button
                                    size="xs"
                                    variant="outline"
                                    onClick={handleNextPage}
                                    disabled={currentPage >= totalPages || isLoadingResult}
                                    rightSection={<IconChevronRight size={16} />}
                                >
                                    {t('extraction.imageViewer.buttons.next')}
                                </Button>
                            </Group>
                        )}
                    </Group>
                </Group>
                <Box className="image-container">
                    {isLoadingResult ?
                        <Loader size={48} />
                        : <Box
                            className="image-dragger"
                            ref={imageContainerRef}
                            onWheel={handleWheel}
                            onMouseDown={handleMouseDown}
                            onMouseMove={handleMouseMove}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            style={{
                                cursor: isDragging ? 'grabbing' : 'grab',
                                overflow: 'hidden'
                            }}
                        >
                            <div className="image-wrapper"
                                style={{
                                    transform: `scale(${zoom}) translate(${pan.x / zoom}px, ${pan.y / zoom}px)`,
                                    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                                    transformOrigin: 'center center',
                                }}
                            >
                                <ImageDisplay
                                    imageUrl={extractedResult?.previewUrl}
                                    isPdf={extractedResult?.isPdf}
                                    currentPage={currentPage}
                                    selectedNode={selectedNode}
                                />
                            </div>
                        </Box>
                    }
                </Box>
                {isDisplayPagination && isGoToPageInvalid && (
                    <Group justify="flex-end">
                        <Text component="span" size="xs" c="red">
                            {t('extraction.imageViewer.pagination.goToPage.invalidPage', { total: totalPages })}
                        </Text>
                    </Group>
                )}
                {isDisplayPagination && selectedNode?.data?.originalPage ? (
                    <Group justify="flex-end" gap="xs" wrap="nowrap">
                        <Tooltip
                            label={t('extraction.imageViewer.pagination.originalPageTooltip')}
                            position="top"
                            withArrow
                        >
                            <Badge
                                variant="filled"
                                color="brand"
                                size="md"
                                radius="sm"
                                leftSection={<IconFileText size={14} />}
                            >
                                {t('extraction.imageViewer.pagination.originalPage', {
                                    page: selectedNode.data.originalPage,
                                })}
                            </Badge>
                        </Tooltip>
                    </Group>
                ) : null}
            </Stack>
        </Card>
    );
};

export default ImageViewer;
